/*
 * Component: Collection Grid Page
 * Description: Advanced product grid with filtering, search, and sorting functionality
 */

// Global collection products data (previously inline script)
var collectionProducts = {
  all: [],
};

class CollectionGridPage {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;

    // Cache DOM elements
    this.productCards = element.querySelectorAll(".product-card");
    this.collectionLinks = element.querySelectorAll(".collection-filter-link");
    this.sortSelect = element.querySelector(".sort-by-dropdown select");
    this.searchInput = element.querySelector(".product-search-input");
    this.searchClear = element.querySelector(".search-clear");
    this.sidebar = element.querySelector(".sidebar-filters");

    // Search state
    this.searchTimeout = null;

    // Store current active collection for re-filtering after DOM changes
    this.currentCollection = null;

    this.init();
  }

  refreshProductCards() {
    this.productCards = this.element.querySelectorAll(".product-card");
  }

  getCollectionFromURL() {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/collections?\/([^\/\?]+)/i);
    return match ? match[1].toLowerCase() : null;
  }

  getFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};

    params.forEach((value, key) => {
      filters[key] = value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    });

    return filters;
  }

  updateURLWithFilters(filters) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([group, values]) => {
      if (values && values.length > 0) {
        params.set(group, values.join(","));
      }
    });

    const newUrl = params.toString()
      ? `${url.pathname}?${params.toString()}`
      : url.pathname;

    window.history.pushState({}, "", newUrl);
  }

  restoreFiltersFromURL() {
    if (!this.sidebar) return;

    const urlFilters = this.getFiltersFromURL();
    const inputs = this.sidebar.querySelectorAll(".sf-input");

    inputs.forEach((input) => (input.checked = false));

    Object.entries(urlFilters).forEach(([group, values]) => {
      values.forEach((value) => {
        const input = Array.from(inputs).find(
          (inp) =>
            inp.dataset.filterGroup === group &&
            inp.value.toLowerCase() === value.toLowerCase()
        );

        if (input) {
          input.checked = true;
        }
      });
    });
  }

  init() {
    this.initImageSliders();
    this.initColorSwatches();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.refreshProductCards();
        this.initCollectionFilters();
      });
    } else {
      setTimeout(() => {
        this.refreshProductCards();
        this.initCollectionFilters();
      }, 0);
    }

    this.initSorting();
    this.initSearch();
    this.initSidebarFilters();
    this.initFullCardClickable();
    this.initPopStateHandler();
  }

  initPopStateHandler() {
    window.addEventListener("popstate", () => {
      this.restoreFiltersFromURL();

      if (this.sidebar) {
        const inputs = this.sidebar.querySelectorAll(".sf-input");
        const groups = {};

        inputs.forEach((input) => {
          if (input.checked) {
            (groups[input.dataset.filterGroup] ||= []).push(input.value);
          }
        });

        this.refreshProductCards();

        const hasFilters = Object.values(groups).some(
          (arr) => arr && arr.length
        );

        this.productCards.forEach((card) => {
          const matchesSidebarFilters =
            !hasFilters || this.matchesFiltersHelper(card, groups);

          let matchesCollection = true;

          if (this.currentCollection) {
            const cardCollections = (
              card.getAttribute("data-collection") || ""
            ).toLowerCase();

            const collectionsArray = cardCollections
              .split(" ")
              .filter((c) => c.length > 0);

            matchesCollection = collectionsArray.includes(
              this.currentCollection.toLowerCase()
            );
          }

          card.style.display =
            matchesSidebarFilters && matchesCollection ? "" : "none";
        });
      }
    });
  }

  matchesFiltersHelper(card, groups) {
    const vendor = this.normalize(card.dataset.vendor);
    const tags = (card.dataset.tags || "").toLowerCase();
    const title = this.normalize(card.dataset.title);
    const type = this.normalize(card.dataset.type);
    const cardCollections = card.getAttribute("data-collection") || "";

    for (const [group, values] of Object.entries(groups)) {
      if (!values.length) continue;

      const any = values.some((v) => {
        const val = v.toLowerCase();

        if (group === "collection") {
          if (val === "all") {
            return true;
          } else {
            const collections = cardCollections
              .split(" ")
              .filter((c) => c.length > 0);

            return collections.includes(val);
          }
        } else if (group === "size") {
          return title.includes(val) || tags.includes(val);
        } else if (group === "color") {
          return title.includes(val) || tags.includes(val);
        } else if (group === "material") {
          return (
            title.includes(val) ||
            tags.includes(val) ||
            type.includes(val)
          );
        } else if (group === "style") {
          return (
            title.includes(val) ||
            tags.includes(val) ||
            type.includes(val)
          );
        } else {
          return (
            vendor.includes(val) ||
            tags.includes(val) ||
            title.includes(val) ||
            type.includes(val)
          );
        }
      });

      if (!any) return false;
    }

    return true;
  }

  initImageSliders() {
    const sliders = this.element.querySelectorAll(".product-slider");

    sliders.forEach((slider) => {
      const wrapper = slider.querySelector(".product-slider-wrapper");
      const slides = slider.querySelectorAll(".product-slide");
      const card = slider.closest(".product-card");
      const dots = card.querySelectorAll(".dot");

      if (slides.length <= 1) return;

      let currentSlide = 0;
      const slideCount = slides.length;

      this.updateDots(dots, currentSlide);

      let touchStartX = 0;
      let touchEndX = 0;

      slider.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      slider.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;

          this.handleSwipe(
            touchStartX,
            touchEndX,
            currentSlide,
            slideCount,
            wrapper,
            dots,
            (newSlide) => {
              currentSlide = newSlide;
            }
          );
        },
        { passive: true }
      );

      this.initMouseDrag(
        slider,
        wrapper,
        dots,
        currentSlide,
        slideCount,
        (newSlide) => {
          currentSlide = newSlide;
        }
      );

      if (dots && dots.length) {
        dots.forEach((dot, i) => {
          dot.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            currentSlide = i;

            this.updateSlider(wrapper, currentSlide);
            this.updateDots(dots, currentSlide);
          });
        });
      }
    });
  }

  initMouseDrag(
    slider,
    wrapper,
    dots,
    currentSlide,
    slideCount,
    setCurrentSlide
  ) {
    let isDragging = false;
    let startPosX = 0;

    slider.addEventListener("mousedown", (e) => {
      e.preventDefault();

      isDragging = true;
      startPosX = e.clientX;
      slider.style.cursor = "grabbing";
    });

    slider.addEventListener("mousemove", (e) => {
      if (!isDragging) return;

      e.preventDefault();

      const moveX = e.clientX - startPosX;
      const slideWidth = slider.offsetWidth;

      if (Math.abs(moveX) < slideWidth * 0.5) {
        wrapper.style.transform = `translateX(${
          -currentSlide * 100 + (moveX / slideWidth) * 100
        }%)`;
      }
    });

    const handleMouseUp = (e) => {
      if (!isDragging) return;

      isDragging = false;
      slider.style.cursor = "grab";

      const moveX = e.clientX - startPosX;
      const threshold = slider.offsetWidth * 0.2;

      if (moveX < -threshold) {
        currentSlide = Math.min(currentSlide + 1, slideCount - 1);
      } else if (moveX > threshold) {
        currentSlide = Math.max(currentSlide - 1, 0);
      }

      setCurrentSlide(currentSlide);

      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    };

    document.addEventListener("mouseup", handleMouseUp);

    document.addEventListener("mouseleave", () => {
      if (isDragging) {
        isDragging = false;
        slider.style.cursor = "grab";

        this.updateSlider(wrapper, currentSlide);
      }
    });
  }

  handleSwipe(
    startX,
    endX,
    currentSlide,
    slideCount,
    wrapper,
    dots,
    setCurrentSlide
  ) {
    const threshold = 50;

    if (startX - endX > threshold) {
      currentSlide = Math.min(currentSlide + 1, slideCount - 1);

      setCurrentSlide(currentSlide);

      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    } else if (endX - startX > threshold) {
      currentSlide = Math.max(currentSlide - 1, 0);

      setCurrentSlide(currentSlide);

      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    }
  }

  updateSlider(wrapper, currentSlide) {
    wrapper.style.transform = `translateX(-${currentSlide * 100}%)`;
  }

  updateDots(dots, currentSlide) {
    if (dots && dots.length) {
      dots.forEach((dot, i) => {
        if (i === currentSlide) {
          dot.classList.add("active");
        } else {
          dot.classList.remove("active");
        }
      });
    }
  }

  initColorSwatches() {
    const swatches = this.element.querySelectorAll(".color-swatch");

    swatches.forEach((swatch) => {
      swatch.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productCard = swatch.closest(".product-card");
        const variantImage = swatch.getAttribute("data-variant-image");
        const cardSwatches = productCard.querySelectorAll(".color-swatch");

        cardSwatches.forEach((s) => s.classList.remove("active"));

        swatch.classList.add("active");

        if (variantImage && variantImage !== "") {
          const productImages = productCard.querySelectorAll(".product-image");

          if (productImages.length > 0) {
            productImages[0].src = variantImage;
          }
        }
      });
    });
  }

  initCollectionFilters() {
    this.refreshProductCards();

    const urlCollection = this.getCollectionFromURL();
    const liquidCollection = this.element.dataset.urlCollection || null;
    let activeLink = this.element.querySelector(
      ".collection-filter-link.active"
    );

    let targetCollection = null;

    if (urlCollection) {
      targetCollection = urlCollection;
    } else if (liquidCollection) {
      targetCollection = liquidCollection;
    } else if (activeLink) {
      targetCollection = activeLink.getAttribute("data-collection");
    }

    if (targetCollection && targetCollection !== "all") {
      const matchingLink = Array.from(this.collectionLinks).find((link) => {
        const linkCollection = link.getAttribute("data-collection");

        return (
          linkCollection &&
          linkCollection.toLowerCase() === targetCollection.toLowerCase()
        );
      });

      if (matchingLink) {
        this.collectionLinks.forEach((l) => l.classList.remove("active"));

        matchingLink.classList.add("active");
        activeLink = matchingLink;
      }
    }

    if (activeLink) {
      const activeCollection = activeLink.getAttribute("data-collection");

      if (activeCollection === "all") {
        this.currentCollection = null;

        this.productCards.forEach((card) => {
          card.style.display = "";
        });
      } else {
        this.currentCollection = activeCollection;

        this.filterProductsByCollection(activeCollection);
      }
    } else if (targetCollection && targetCollection !== "all") {
      this.currentCollection = targetCollection;

      this.filterProductsByCollection(targetCollection);
    } else {
      this.currentCollection = null;

      this.productCards.forEach((card) => {
        card.style.display = "";
      });
    }

    this.collectionLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();

        this.collectionLinks.forEach((l) => l.classList.remove("active"));

        link.classList.add("active");

        const collectionHandle = link.getAttribute("data-collection");

        this.currentCollection =
          collectionHandle === "all" ? null : collectionHandle;

        this.refreshProductCards();

        if (collectionHandle === "all") {
          this.productCards.forEach((card) => {
            card.style.display = "";
          });
        } else {
          this.filterProductsByCollection(collectionHandle);
        }

        if (this.searchInput && this.searchInput.value.trim() !== "") {
          this.searchInput.value = "";

          if (this.searchClear) {
            this.searchClear.style.display = "none";
          }

          this.element.classList.remove("searching");
        }
      });
    });
  }

  filterProductsByCollection(collectionHandle) {
    this.refreshProductCards();

    if (!collectionHandle || collectionHandle === "all") {
      this.productCards.forEach((card) => {
        card.style.display = "";
      });

      return;
    }

    const handle = collectionHandle.toLowerCase();

    this.productCards.forEach((card) => {
      const cardCollections = (
        card.getAttribute("data-collection") || ""
      ).toLowerCase();

      const collectionsArray = cardCollections
        .split(" ")
        .filter((c) => c.length > 0);

      const shouldShow = collectionsArray.includes(handle);

      card.style.display = shouldShow ? "" : "none";
    });
  }

  initSorting() {
    if (!this.sortSelect) return;

    this.sortSelect.addEventListener("change", () => {
      this.refreshProductCards();

      const selectedSort = this.sortSelect.value;
      const productGrid = this.element.querySelector(".product-grid");

      const visibleProducts = Array.from(this.productCards).filter(
        (card) => card.style.display !== "none"
      );

      visibleProducts.sort((a, b) => {
        switch (selectedSort) {
          case "price-ascending":
            return (
              parseFloat(a.getAttribute("data-price")) -
              parseFloat(b.getAttribute("data-price"))
            );

          case "price-descending":
            return (
              parseFloat(b.getAttribute("data-price")) -
              parseFloat(a.getAttribute("data-price"))
            );

          case "title-ascending":
            return a
              .getAttribute("data-title")
              .localeCompare(b.getAttribute("data-title"));

          case "title-descending":
            return b
              .getAttribute("data-title")
              .localeCompare(a.getAttribute("data-title"));

          case "created-ascending":
            return (
              new Date(a.getAttribute("data-created")) -
              new Date(b.getAttribute("data-created"))
            );

          case "created-descending":
            return (
              new Date(b.getAttribute("data-created")) -
              new Date(a.getAttribute("data-created"))
            );

          default:
            return (
              parseInt(a.getAttribute("data-index")) -
              parseInt(b.getAttribute("data-index"))
            );
        }
      });

      visibleProducts.forEach((product) => {
        productGrid.appendChild(product);
      });

      if (this.currentCollection) {
        setTimeout(() => {
          this.refreshProductCards();

          this.filterProductsByCollection(this.currentCollection);
        }, 0);
      }
    });
  }

  initSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener("input", (e) => {
      const searchTerm = e.target.value.toLowerCase().trim();

      if (searchTerm.length > 0) {
        if (this.searchClear) {
          this.searchClear.style.display = "flex";
        }

        this.element.classList.add("searching");
      } else {
        if (this.searchClear) {
          this.searchClear.style.display = "none";
        }

        this.element.classList.remove("searching");
      }

      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(() => {
        this.performSearch(searchTerm);
      }, 300);
    });

    if (this.searchClear) {
      this.searchClear.addEventListener("click", () => {
        this.searchInput.value = "";
        this.searchClear.style.display = "none";

        this.element.classList.remove("searching");

        this.showAllProducts();
      });
    }
  }

  performSearch(searchTerm) {
    this.refreshProductCards();

    if (searchTerm === "") {
      if (this.currentCollection) {
        this.filterProductsByCollection(this.currentCollection);
      } else {
        this.showAllProducts();
      }

      return;
    }

    this.productCards.forEach((card) => {
      const title = card.getAttribute("data-title")?.toLowerCase() || "";
      const vendor = card.getAttribute("data-vendor")?.toLowerCase() || "";
      const tags = card.getAttribute("data-tags")?.toLowerCase() || "";
      const type = card.getAttribute("data-type")?.toLowerCase() || "";

      const isMatch =
        title.includes(searchTerm) ||
        vendor.includes(searchTerm) ||
        tags.includes(searchTerm) ||
        type.includes(searchTerm);

      let matchesCollection = true;

      if (this.currentCollection) {
        const cardCollections = (
          card.getAttribute("data-collection") || ""
        ).toLowerCase();

        const collectionsArray = cardCollections
          .split(" ")
          .filter((c) => c.length > 0);

        matchesCollection = collectionsArray.includes(
          this.currentCollection.toLowerCase()
        );
      }

      if (isMatch && matchesCollection) {
        card.classList.remove("hidden");
        card.style.display = "";
      } else {
        card.classList.add("hidden");

        setTimeout(() => {
          if (card.classList.contains("hidden")) {
            card.style.display = "none";
          }
        }, 300);
      }
    });
  }

  showAllProducts() {
    this.refreshProductCards();

    if (this.currentCollection) {
      this.filterProductsByCollection(this.currentCollection);
    } else {
      this.productCards.forEach((card) => {
        card.classList.remove("hidden");
        card.style.display = "";
      });
    }
  }

  initSidebarFilters() {
    if (!this.sidebar) return;

    const clearBtn = this.sidebar.querySelector("[data-clear-filters]");
    const inputs = this.sidebar.querySelectorAll(".sf-input");
    const header = this.sidebar.querySelector(".sidebar-filters__header");

    this.restoreFiltersFromURL();

    const initMobileDropdown = () => {
      if (window.innerWidth <= 990) {
        this.sidebar.classList.add("collapsed");
      } else {
        this.sidebar.classList.remove("collapsed");
      }
    };

    if (header) {
      header.addEventListener("click", (e) => {
        if (window.innerWidth <= 990) {
          if (
            !e.target.matches(".sidebar-filters__clear") &&
            !e.target.closest(".sidebar-filters__clear")
          ) {
            e.preventDefault();

            this.sidebar.classList.toggle("collapsed");
          }
        }
      });
    }

    initMobileDropdown();

    window.addEventListener("resize", initMobileDropdown);

    const getActiveFilters = () => {
      const groups = {};

      inputs.forEach((input) => {
        if (input.checked) {
          (groups[input.dataset.filterGroup] ||= []).push(input.value);
        }
      });

      return groups;
    };

    const matchesFilters = (card, groups) => {
      const vendor = this.normalize(card.dataset.vendor);
      const tags = (card.dataset.tags || "").toLowerCase();
      const title = this.normalize(card.dataset.title);
      const type = this.normalize(card.dataset.type);
      const cardCollections = card.getAttribute("data-collection") || "";

      for (const [group, values] of Object.entries(groups)) {
        if (!values.length) continue;

        const any = values.some((v) => {
          const val = v.toLowerCase();

          if (group === "collection") {
            if (val === "all") {
              return true;
            } else {
              const collections = cardCollections
                .split(" ")
                .filter((c) => c.length > 0);

              return collections.includes(val);
            }
          } else if (group === "size") {
            return title.includes(val) || tags.includes(val);
          } else if (group === "color") {
            return title.includes(val) || tags.includes(val);
          } else if (group === "material") {
            return (
              title.includes(val) ||
              tags.includes(val) ||
              type.includes(val)
            );
          } else if (group === "style") {
            return (
              title.includes(val) ||
              tags.includes(val) ||
              type.includes(val)
            );
          } else {
            return (
              vendor.includes(val) ||
              tags.includes(val) ||
              title.includes(val) ||
              type.includes(val)
            );
          }
        });

        if (!any) return false;
      }

      return true;
    };

    const applyFilters = (updateURL = false) => {
      this.refreshProductCards();

      const groups = getActiveFilters();

      const hasFilters = Object.values(groups).some(
        (arr) => arr && arr.length
      );

      if (updateURL) {
        this.updateURLWithFilters(groups);
      }

      this.productCards.forEach((card) => {
        const matchesSidebarFilters =
          !hasFilters || matchesFilters(card, groups);

        let matchesCollection = true;

        if (this.currentCollection) {
          const cardCollections = (
            card.getAttribute("data-collection") || ""
          ).toLowerCase();

          const collectionsArray = cardCollections
            .split(" ")
            .filter((c) => c.length > 0);

          matchesCollection = collectionsArray.includes(
            this.currentCollection.toLowerCase()
          );
        }

        card.style.display =
          matchesSidebarFilters && matchesCollection ? "" : "none";
      });
    };

    const applyBtn = this.element.querySelector("[data-apply-filters]");

    if (applyBtn) {
      applyBtn.addEventListener("click", () => {
        applyFilters(true);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        inputs.forEach((input) => (input.checked = false));

        this.updateURLWithFilters({});

        applyFilters(false);
      });
    }

    setTimeout(() => applyFilters(false), 40);
  }

  initFullCardClickable() {
    const clickableCards = this.element.querySelectorAll(
      ".product-card.full-card-clickable"
    );

    clickableCards.forEach((card) => {
      const productUrl = card.getAttribute("data-product-url");

      if (!productUrl) return;

      let isSliding = false;
      let startX = 0;
      let startY = 0;
      const slideThreshold = 10;

      const handleStart = (e) => {
        isSliding = false;

        const touch = e.touches ? e.touches[0] : e;

        startX = touch.clientX;
        startY = touch.clientY;
      };

      const handleMove = (e) => {
        if (!startX || !startY) return;

        const touch = e.touches ? e.touches[0] : e;

        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);

        if (deltaX > slideThreshold || deltaY > slideThreshold) {
          isSliding = true;
        }
      };

      const handleCardClick = (e) => {
        if (isSliding) return;

        const clickedElement = e.target;

        if (
          clickedElement.closest(
            "a, button, input, select, textarea"
          )
        ) {
          return;
        }

        if (clickedElement.closest(".product-dots, .dot")) {
          return;
        }

        window.location.href = productUrl;
      };

      card.addEventListener("mousedown", handleStart);
      card.addEventListener("mousemove", handleMove);
      card.addEventListener("click", handleCardClick);

      card.addEventListener("touchstart", handleStart, {
        passive: true,
      });

      card.addEventListener("touchmove", handleMove, {
        passive: true,
      });

      card.addEventListener(
        "touchend",
        () => {
          setTimeout(() => {
            isSliding = false;
          }, 100);
        },
        { passive: true }
      );

      card.addEventListener("mouseup", () => {
        setTimeout(() => {
          isSliding = false;
        }, 100);
      });
    });
  }

  normalize(str) {
    return (str || "")
      .toString()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  destroy() {
    clearTimeout(this.searchTimeout);
  }
}

function initCollectionGridPageElement(element) {
  if (!element || element.__collectionGridPageInstance) return;

  element.__collectionGridPageInstance =
    new CollectionGridPage(element);
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll('[data-component="collection-grid-page"]')
    .forEach(initCollectionGridPageElement);
});

document.addEventListener("shopify:section:load", (event) => {
  const components = event.target.querySelectorAll(
    '[data-component="collection-grid-page"]'
  );

  components.forEach(initCollectionGridPageElement);
});


/* ============================================================
   CanvasSecret — Show More primary artworks across Shopify pages
   ============================================================ */

(function () {
  "use strict";

  function getCardKey(card) {
    return (
      card.getAttribute("data-product-handle") ||
      card.getAttribute("data-product-url") ||
      card.getAttribute("data-title") ||
      ""
    );
  }

  function initShowMore(component) {
    if (
      !component ||
      component.dataset.csShowMoreInitialized === "true"
    ) {
      return;
    }

    const marker = component.querySelector(".cs-load-more-marker");
    const grid = component.querySelector(".product-grid");

    if (!marker || !grid) return;

    let nextUrl = marker.dataset.nextUrl || "";

    if (!nextUrl) return;

    component.dataset.csShowMoreInitialized = "true";

    const wrap = document.createElement("div");
    wrap.className = "cs-load-more-wrap";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "cs-load-more-button";
    button.textContent = "Show More";

    wrap.appendChild(button);

    grid.insertAdjacentElement("afterend", wrap);

    let loading = false;

    async function fetchPage(url) {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }

      const html = await response.text();

      const doc = new DOMParser().parseFromString(
        html,
        "text/html"
      );

      const sectionId = component.dataset.sectionId;

      return (
        (sectionId &&
          doc.querySelector(
            '[data-component="collection-grid-page"][data-section-id="' +
              CSS.escape(sectionId) +
              '"]'
          )) ||
        doc.querySelector(
          '[data-component="collection-grid-page"]'
        )
      );
    }

    function appendPrimaryCards(sourceComponent) {
      if (!sourceComponent) return 0;

      const sourceGrid =
        sourceComponent.querySelector(".product-grid");

      if (!sourceGrid) return 0;

      const existing = new Set(
        Array.from(
          grid.querySelectorAll(":scope > .product-card")
        )
          .map(getCardKey)
          .filter(Boolean)
      );

      let added = 0;

      sourceGrid
        .querySelectorAll(":scope > .product-card")
        .forEach((card) => {
          const key = getCardKey(card);

          if (key && existing.has(key)) {
            return;
          }

          grid.appendChild(
            document.importNode(card, true)
          );

          if (key) {
            existing.add(key);
          }

          added++;
        });

      const instance =
        component.__collectionGridPageInstance;

      if (
        instance &&
        typeof instance.refreshProductCards === "function"
      ) {
        instance.refreshProductCards();
      }

      return added;
    }

    async function loadMore() {
      if (loading || !nextUrl) return;

      loading = true;

      button.disabled = true;
      button.textContent = "Loading…";

      try {
        let added = 0;
        let guard = 0;

        while (
          nextUrl &&
          added === 0 &&
          guard < 20
        ) {
          guard++;

          const sourceComponent =
            await fetchPage(nextUrl);

          if (!sourceComponent) {
            throw new Error(
              "Collection section not found"
            );
          }

          added += appendPrimaryCards(
            sourceComponent
          );

          const sourceMarker =
            sourceComponent.querySelector(
              ".cs-load-more-marker"
            );

          nextUrl = sourceMarker
            ? sourceMarker.dataset.nextUrl || ""
            : "";

          marker.dataset.nextUrl = nextUrl;
        }

        const instance =
          component.__collectionGridPageInstance;

        if (instance) {
          if (
            typeof instance.refreshProductCards ===
            "function"
          ) {
            instance.refreshProductCards();
          }

          if (
            typeof instance.initCollectionFilters ===
            "function"
          ) {
            instance.initCollectionFilters();
          }
        }

        if (!nextUrl) {
          wrap.remove();
          return;
        }

        button.disabled = false;
        button.textContent = "Show More";
      } catch (error) {
        console.error(
          "CanvasSecret Show More error:",
          error
        );

        button.disabled = false;
        button.textContent = "Try Again";
      } finally {
        loading = false;
      }
    }

    button.addEventListener("click", loadMore);
  }

  function initAllShowMore(root) {
    (root || document)
      .querySelectorAll(
        '[data-component="collection-grid-page"]'
      )
      .forEach(initShowMore);
  }

  document.addEventListener(
    "DOMContentLoaded",
    () => initAllShowMore(document)
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => initAllShowMore(event.target)
  );
})();

/* ============================================================
   CanvasSecret — Full Catalog Search
   Automatically load remaining products when search is used
   ============================================================ */

(function () {
  "use strict";

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function waitForShowMore(button, wrapper) {
    let guard = 0;

    while (guard < 300) {
      guard++;

      // Show More disappeared = catalog fully loaded
      if (!wrapper.isConnected || !button.isConnected) {
        return;
      }

      // Current fetch completed and button is usable again
      if (
        !button.disabled &&
        button.textContent.trim() !== "Loading…"
      ) {
        return;
      }

      await sleep(50);
    }
  }

  async function loadEntireCatalog(component) {
    if (component.dataset.csFullCatalogLoaded === "true") {
      return;
    }

    if (component.dataset.csFullCatalogLoading === "true") {
      // Another search already triggered loading.
      while (
        component.dataset.csFullCatalogLoading === "true"
      ) {
        await sleep(100);
      }

      return;
    }

    component.dataset.csFullCatalogLoading = "true";

    try {
      let safety = 0;

      while (safety < 50) {
        safety++;

        const wrapper = component.querySelector(
          ".cs-load-more-wrap"
        );

        const button = component.querySelector(
          ".cs-load-more-button"
        );

        // No Show More button = no more backend pages
        if (!wrapper || !button) {
          component.dataset.csFullCatalogLoaded = "true";
          break;
        }

        // Wait if another request is currently running
        if (button.disabled) {
          await waitForShowMore(button, wrapper);
          continue;
        }

        button.click();

        await waitForShowMore(button, wrapper);

        // Give DOM + collection instance a moment to refresh
        await sleep(75);
      }

      if (
        !component.querySelector(".cs-load-more-button")
      ) {
        component.dataset.csFullCatalogLoaded = "true";
      }
    } catch (error) {
      console.error(
        "CanvasSecret full catalog search loading error:",
        error
      );
    } finally {
      component.dataset.csFullCatalogLoading = "false";
    }
  }

  function initFullSearch(component) {
    if (
      !component ||
      component.dataset.csFullSearchInitialized === "true"
    ) {
      return;
    }

    const searchInput = component.querySelector(
      ".product-search-input"
    );

    if (!searchInput) {
      return;
    }

    component.dataset.csFullSearchInitialized = "true";

    let requestId = 0;

    searchInput.addEventListener("input", async () => {
      const term = searchInput.value
        .toLowerCase()
        .trim();

      requestId++;

      const thisRequest = requestId;

      if (!term) {
        return;
      }

      /*
       * Hide Show More while searching.
       * Search should behave like a full-catalog search,
       * not a paginated browse experience.
       */
      const currentWrapper = component.querySelector(
        ".cs-load-more-wrap"
      );

      if (currentWrapper) {
        currentWrapper.style.display = "none";
      }

      /*
       * Load every remaining primary artwork.
       */
      await loadEntireCatalog(component);

      /*
       * User may have changed the query while loading.
       */
      if (thisRequest !== requestId) {
        return;
      }

      const instance =
        component.__collectionGridPageInstance;

      if (!instance) {
        return;
      }

      /*
       * Refresh references because new cards were injected.
       */
      if (
        typeof instance.refreshProductCards === "function"
      ) {
        instance.refreshProductCards();
      }

      /*
       * Re-run search against the COMPLETE catalog.
       */
      if (
        typeof instance.performSearch === "function"
      ) {
        instance.performSearch(
          searchInput.value.toLowerCase().trim()
        );
      }
    });

    /*
     * When search is cleared:
     * - if catalog isn't fully loaded, restore Show More
     * - if catalog IS fully loaded, no button is needed anymore
     */
    searchInput.addEventListener("input", () => {
      const term = searchInput.value.trim();

      if (term !== "") {
        return;
      }

      const wrapper = component.querySelector(
        ".cs-load-more-wrap"
      );

      if (
        wrapper &&
        component.dataset.csFullCatalogLoaded !== "true"
      ) {
        wrapper.style.display = "";
      }
    });
  }

  function initAll(root) {
    (root || document)
      .querySelectorAll(
        '[data-component="collection-grid-page"]'
      )
      .forEach(initFullSearch);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll(document)
    );
  } else {
    initAll(document);
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => initAll(event.target)
  );
})();